import type { ExportChoice, ExportFormat } from '@brush/api-client';

/**
 * The export control's rules (docs/specs/export.md EX14, EX15, EX24, EX30, §6), as a pure module with tests, because
 * each fails quietly: a remembered cards choice puts card IDs in a file nobody asked to carry them, an export offered
 * where nothing can be published is a click answered by a refusal, a refusal worded as a fault sends an operator to the
 * wrong place, and a printed page that does not say it is one of several reads as the whole list.
 *
 * Type-only imports, so `node --test` runs it.
 */

/** The three formats (EX8), worded for an operator: a spreadsheet is "Excel", whatever its extension says. */
export const EXPORT_FORMATS: readonly { value: ExportFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel' },
  { value: 'json', label: 'JSON' },
];

/** EX27's cap, named only when the server's refusal carries no sentence of its own. */
export const EXPORT_MAX_ROWS = 25_000;

/**
 * The choice the control opens with. **Card IDs start unticked every time** (EX15) — a choice remembered from the last
 * download is how a file nobody meant to carry cards ends up carrying them. The format is kept: asking for Excel twice
 * running is the ordinary case, and a format carries nobody's identity.
 */
export function freshChoice(last?: ExportChoice): ExportChoice {
  return { format: last?.format ?? 'csv', cardIds: false };
}

/** What a view knows about itself that decides whether its export can answer. */
export type ExportAvailability =
  | { view: 'T1' | 'T2' | 'F2' }
  | { view: 'F3'; status: string }
  | { view: 'L'; status: string }
  | { view: 'R'; status: string; isTemplate: boolean }
  | { view: 'F1'; status: string };

/**
 * EX30: the control is shown only where the export can answer. A Draft board and a contest that has not locked have
 * published nothing, and a platform template has no room and so nothing to score (EX31). The four lists answer at every
 * status: an empty filter, a tournament with no results, a contest nobody entered and an empty pool are true answers —
 * a file with its heading row.
 *
 * A started board that has not yet recalculated is offered: its status cannot tell, and the server's refusal says what
 * to do (`exportRefusal`).
 */
export function exportOffered(availability: ExportAvailability): boolean {
  switch (availability.view) {
    case 'L':
      return availability.status !== 'draft';
    case 'R':
      return !availability.isTemplate && availability.status !== 'draft';
    case 'F1':
      return availability.status === 'locked' || availability.status === 'closed';
    default:
      return true;
  }
}

/**
 * A sentence beside the control saying what the file holds, where that differs from what the screen shows — or null.
 * One case today: a Draft contest's pool (F3, EX7). The export reads the pool as stored, and the pool review edits a
 * draft of it, so an operator mid-edit would otherwise download the prices they have just changed away from. Once
 * the contest publishes the pool is frozen and what is on screen is what is saved.
 */
export function exportNote(availability: ExportAvailability): string | null {
  if (availability.view === 'F3' && availability.status === 'draft')
    return 'Exports the saved pool; save your changes first to include them.';
  return null;
}

/** The fields of an `ApiError` this reads — structurally, so the module needs no runtime import. */
export interface RefusalLike {
  status?: number;
  code?: string;
  serverMessage?: string;
}

function asRefusal(error: unknown): RefusalLike | null {
  if (!error || typeof error !== 'object' || typeof (error as RefusalLike).status !== 'number') return null;
  return error as RefusalLike;
}

/**
 * An export's own refusals in plain words (§5), or null for anything else, which `describeApiError` words — so this
 * never asserts a cause it cannot know.
 *
 * - Nothing published leads with what to do, per view (EX30). The control is not offered on a Draft, so this is a
 *   started board that has not recalculated, or a locked contest whose leaderboard has not.
 * - Too large keeps the server's sentence, which names the cap the server actually applied and the count; the client's
 *   names EX27's cap only when the server sent none.
 * - A 403 says who may export: an empty body is the policy (no staff role at all), `room_not_operated` a reader of
 *   the room who does not operate it.
 */
export function exportRefusal(view: string, error: unknown): string | null {
  const refusal = asRefusal(error);
  if (!refusal) return null;

  if (refusal.status === 409 && refusal.code === 'standings_not_published') {
    if (view === 'R') return 'Nothing is published yet; recalculate the rating first, then export.';
    if (view === 'F1')
      return 'Nothing is published yet; standings appear once the contest locks and its leaderboard recalculates.';
    return 'Nothing is published yet; recalculate the leaderboard first, then export.';
  }

  if (refusal.status === 409 && refusal.code === 'rating_board_is_a_template')
    return 'A platform template has no standings of its own. Export a rating made from it.';

  if (refusal.code === 'export_too_large')
    return (
      refusal.serverMessage ??
      `An export holds at most ${EXPORT_MAX_ROWS.toLocaleString('en-US')} rows, and this one would hold more. ` +
        'Narrow the filter and export again.'
    );

  if (refusal.status === 403) {
    if (refusal.code === 'room_not_operated')
      return "Only this room's operators can export it, and you don't operate this room.";
    if (!refusal.code) return 'Exports are for room staff, and this sign-in holds no staff role.';
  }

  return null;
}

/** Which page of the filtered list is on screen, and of how many (an empty list is page 1 of 1). */
export interface PrintPage {
  page: number;
  pageCount: number;
}

export function pageOf(page: number, pageSize: number, total: number): PrintPage {
  return { page, pageCount: Math.max(1, Math.ceil(total / Math.max(1, pageSize))) };
}

/**
 * The line a printed view carries above its table (§6, Answered 13a): the room, the view, the moment printed in the
 * room's zone with a named month — and, for T1, which page of the filtered list this is, because print shows what the
 * screen shows and the screen shows one page.
 */
export function printLine(line: {
  roomName: string;
  viewLabel: string;
  printedAt: Date;
  timeZone: string;
  page?: PrintPage;
}): string {
  const moment = new Intl.DateTimeFormat('en-US', {
    timeZone: line.timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(line.printedAt);
  const parts = [line.roomName, line.viewLabel, `printed ${moment}`];
  if (line.page) parts.push(`page ${line.page.page} of ${line.page.pageCount} of the filtered list`);
  return parts.join(' · ');
}

/**
 * The file's name when the response carries no `Content-Disposition` (EX24), slugged as the server slugs: accents
 * dropped, lower case, every run of anything else a single hyphen, an empty part left out. The server's name is the
 * one used whenever it arrives; this is only the fallback, and it carries no date because the client does not know the
 * room's gaming day.
 */
export function fallbackFileName(parts: string[], format: ExportFormat): string {
  const slug = (part: string) =>
    part
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/g, '');
  return `${parts.map(slug).filter(Boolean).join('-')}.${format}`;
}
